import { Box, BoxProps, Heading, HStack, Text, VStack } from "@biblioteksentralen/react";
import { DigitalLibraryServiceCategory } from "@libry-content/types";
import { PropsWithChildren } from "react";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { Edit } from "../editInSanity/EditInSanity";
import { SanityImageFillAvailableSpace } from "../images/SanityImageFillAvailableSpace";
import InternalLinkOverlay from "../InternalLinkOverlay";
import { ResolvedDigitalLibraryServiceSummary } from "@libry-content/common";
import { ServiceCategoryIcon } from "./ServiceCategoryIcon";
import { useDigitalServiceColor } from "./useDigitalServiceColor";

type DigitalLibraryServicePreviewMobileProps = {
  digitalLibraryService: ResolvedDigitalLibraryServiceSummary;
  category: DigitalLibraryServiceCategory;
  href: string;
};

export const DigitalLibraryServicePreviewMobile = ({
  digitalLibraryService,
  category,
  href,
  ...boxProps
}: DigitalLibraryServicePreviewMobileProps & BoxProps) => {
  const { ts } = useTranslation();
  const paletteColor = useDigitalServiceColor(digitalLibraryService);

  return (
    <HStack alignItems="flex-start" spacing="1rem" {...boxProps}>
      {!!digitalLibraryService.image?.asset && (
        <ServiceImageContainer>
          <SanityImageFillAvailableSpace
            image={digitalLibraryService.image}
            resolution={1080}
            hideCaption
            _groupHover={{ transform: "unset" }}
          />
        </ServiceImageContainer>
      )}

      {!digitalLibraryService.image?.asset && (
        <ServiceImageContainer padding="1rem" {...paletteColor?.css}>
          <ServiceCategoryIcon width="100%" height="100%" category={category} background="none" />
        </ServiceImageContainer>
      )}

      <VStack alignItems="flex-start" spacing="0">
        <Heading as="h3" fontSize="xl" marginBottom="0.125rem">
          <InternalLinkOverlay href={href} wordBreak={{ base: "break-all", sm: undefined }}>
            {ts(digitalLibraryService.name)}
          </InternalLinkOverlay>
        </Heading>
        <Text>{ts(digitalLibraryService.teaser)}</Text>
        <Edit doc={digitalLibraryService} />
      </VStack>
    </HStack>
  );
};

export const ServiceImageContainer = ({ children, ...boxProps }: PropsWithChildren & BoxProps) => (
  <Box
    position="relative"
    width={{ base: "5rem", sm: "7rem" }}
    minWidth={{ base: "5rem", sm: "7rem" }}
    height={{ base: "5rem", sm: "7rem" }}
    minHeight={{ base: "5rem", sm: "7rem" }}
    borderRadius="lg"
    overflow="hidden"
    {...boxProps}
  >
    {children}
  </Box>
);
